import { useEffect, useState } from "react";

import { adminApi, type AuditItem } from "@/api/admin";
import { ApiError } from "@/api/client";

export default function AuditPage() {
  const [actor, setActor] = useState("");
  const [actionType, setActionType] = useState("");
  const [items, setItems] = useState<AuditItem[]>([]);
  const [cursor, setCursor] = useState<{ ts: string | null; id: number } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = (reset: boolean) => {
    setLoading(true);
    setError(null);
    adminApi
      .audit({
        actor: actor.trim() || undefined,
        action_type: actionType.trim() || undefined,
        cursor_ts: reset ? null : cursor?.ts ?? null,
        cursor_id: reset ? null : cursor?.id ?? null,
      })
      .then((r) => {
        setItems((prev) => (reset ? r.items : [...prev, ...r.items]));
        setCursor(r.next_cursor);
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCursor(null);
    load(true);
  };

  return (
    <div className="space-y-3">
      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          placeholder="按 actor open_id 过滤"
          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-sky-400"
        />
        <input
          value={actionType}
          onChange={(e) => setActionType(e.target.value)}
          placeholder="按 action 过滤 (如 force_delete_favorite, forward)"
          className="flex-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-sky-400"
        />
        <button
          type="submit"
          className="rounded-full bg-slate-900 px-4 py-1.5 text-sm text-white"
        >
          查询
        </button>
      </form>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="space-y-1">
        {items.map((it) => {
          const key = `${it.source}:${it.id}`;
          const isOpen = expanded === key;
          return (
            <div
              key={key}
              className="rounded-lg border border-slate-200 bg-white text-sm"
            >
              <button
                onClick={() => setExpanded(isOpen ? null : key)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${
                    it.source === "admin"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-sky-100 text-sky-800"
                  }`}
                >
                  {it.source}
                </span>
                <span className="font-medium text-slate-700">{it.action}</span>
                <span className="font-mono text-xs text-slate-500">
                  {it.actor || "—"}
                </span>
                <span className="truncate text-xs text-slate-400">
                  {it.target_type ? `${it.target_type}:` : ""}
                  {it.target_id || ""}
                </span>
                {it.status === "failed" || it.error ? (
                  <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700">
                    failed
                  </span>
                ) : null}
                <span className="ml-auto text-xs text-slate-400">
                  {new Date(it.ts).toLocaleString("zh-CN")}
                </span>
              </button>
              {isOpen ? (
                <div className="border-t border-slate-100 px-3 py-2 text-xs">
                  {it.reason ? (
                    <div>
                      <span className="text-slate-500">原因：</span>
                      {it.reason}
                    </div>
                  ) : null}
                  {it.error ? (
                    <div className="text-rose-700">
                      <span className="text-slate-500">错误：</span>
                      {it.error}
                    </div>
                  ) : null}
                  {it.payload ? (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-500">
                        payload (snapshot)
                      </summary>
                      <pre className="mt-1 max-h-64 overflow-auto rounded bg-slate-50 p-2 text-[11px]">
                        {JSON.stringify(it.payload, null, 2)}
                      </pre>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {!loading && items.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">无记录</div>
        ) : null}
      </div>

      {cursor ? (
        <div className="text-center">
          <button
            onClick={() => load(false)}
            disabled={loading}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600 disabled:opacity-50"
          >
            {loading ? "加载中…" : "加载更多"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
